"use client";
import CommonTitle from "@/components/common/CommonTitle";
import DetailWrapper from "@/components/tools/ChatWithAnything/Detail";
import { StaticImageData } from "next/image";
import AiToolsFaq, {
  IFaqList,
} from "@/components/tools/ToolsCommon/AiToolsFaq";
import WorksPDF, {
  WorksPDFSectionProps,
} from "@/components/tools/ToolsCommon/WorksPDF";
import SmartChat, {
  SmartChatSectionProps,
} from "@/components/tools/ToolsCommon/SmartChat";
import MajorChat, {
  MajorChatSectionProps,
} from "@/components/tools/ToolsCommon/MajorChat";
import ChatWithPdfTable from "@/components/tools/ToolsCommon/ChatWithPdfTable";
import PdfDemo from "@/components/tools/ToolsCommon/PdfDemo";

interface PageInfoProps {
  title: string;
  titleHighlight: string;
}

export type TAIPropTypes = {
  params: {
    pageInfo: PageInfoProps;
    faqList: IFaqList[];
    stepsSectionFeatures: WorksPDFSectionProps[];
    smartChatSectionFeatures: SmartChatSectionProps[];
    majorChatSectionFeatures: MajorChatSectionProps[];
    compareSection: {
      products: {
        productName: string;
        productLogo?: StaticImageData;
        productLogo2?: StaticImageData;
      }[];
      compareTable: {
        feature: string;
        value: boolean[];
      }[];
    };
  };
};

export type TFeatureList = {
  title: string;
  subtitle: string;
  featuredImg: StaticImageData;
};

export type ChatWithPdfTableType = {
  products: {
    productName: string;
    productTitle?: string;
    productLogo?: StaticImageData;
    productTitle1?: string;
    productLogo2?: StaticImageData;
    productTitle2?: string;
  }[];
  compareTable: {
    feature: string;
    value: boolean[];
  }[];
};
const usecase = "chat-with-pdf";

const ChatWithPdfRoot = ({ params }: TAIPropTypes) => {
  const {
    pageInfo,
    stepsSectionFeatures,
    smartChatSectionFeatures,
    majorChatSectionFeatures,
    compareSection,
    faqList,
  } = params;

  return (
    <section className="pb-8 pt-24 sm:pt-20 md:pt-24 lg:pt-28 xl:pb-12 xl:pt-[90px] 2xl:pt-[110px]">
      <div className="container w-full px-4 text-center sm:px-6">
        <h1 className="text-[32px] font-bold leading-tight text-[#202020] xl:text-[48px]">
          {pageInfo.title}{" "}
          <span className="inline-flex items-center rounded-xl bg-gradient-to-r from-purple-500 to-purple-600 px-4 py-1 text-white shadow-lg">
            {pageInfo.titleHighlight}
          </span>
        </h1>
        <div className="mx-auto max-w-2xl">
          <p className="text-[16px] font-normal leading-relaxed text-gray-600">
            Join millions of{" "}
            <span className="border-b-2 border-purple-300 font-medium text-purple-500">
              students, researchers and professionals
            </span>{" "}
            to instantly answer questions and understand research with AI
          </p>
        </div>
      </div>
      <DetailWrapper usecase={usecase} />
      <WorksPDF
        title="How Kroolo’s ChatPDF Works?"
        subtitle="It's easy to use Kroolo's free chat with any PDF tool. All you have to do is submit a PDF file of any word count, and you're ready to go. You can ask questions, summarize text, extract table essence, etc. from chatpdf in a go."
        featureArray={stepsSectionFeatures}
      />
      <SmartChat
        title="ChatPDF in a Nutshell"
        subtitle="Kroolo’s Chat with any PDF lets users quickly interact with PDF documents via chat, offering instant insights and helpful features without scrolling."
        featureArray={smartChatSectionFeatures}
      />
      <MajorChat
        title="Anyone can use Chat with PDF"
        subtitle="Ideal for anyone looking to simplify the process of identifying key information within PDF documents. Both professionals and academics can leverage its chat-based capabilities for more efficient document analysis."
        featureArray={majorChatSectionFeatures}
      />
      <div className="container pt-8 xl:pt-12">
        <div className="mx-auto">
          <div className="mx-auto space-y-4 pb-5 xl:px-20 xl:pb-8">
            <div className="mx-auto text-center sm:max-w-[900px] xl:max-w-[1180px]">
              <CommonTitle
                as="h2"
                fontWeightClass="font-semibold"
                fontSizeClass="text-[28px] xl:text-[32px]"
                color="#202020"
              >
                How we compare with others
              </CommonTitle>
            </div>
          </div>
        </div>
      </div>
      <ChatWithPdfTable productInfo={compareSection} />
      <PdfDemo />
      <AiToolsFaq faqTitle="FAQs: Chat with Pdf" faqList={faqList} />
    </section>
  );
};

export default ChatWithPdfRoot;
